// FRE-293: thin "do we have any files yet?" probe.
//
// The drawer's section 1 and the empty-state card both need to know whether
// the current org has at least one uploaded file so the section can either
// auto-collapse (≥1) or render expanded (0). We hit `/api/v1/files?limit=1`
// and cache the boolean in-memory with a short TTL — the result is cheap to
// re-fetch and the cache is just to keep the UI snappy across re-mounts
// (drawer close/open, route navigation back to /).
//
// Strategy (b) from the FRE-293 plan: section is self-contained, doesn't push
// the count through the root loader. The route at `/` still owns its own full
// listing — this cache is only the "any files at all" signal.

import { apiGet } from "@/lib/api.ts";

// Worker response shape for `/api/v1/files`. We don't import the full type from
// the route to avoid a cross-app dep — only `items.length > 0` matters here.
type FilesResponse = { items: unknown[] };

type CacheEntry = {
  hasFiles: boolean;
  fetchedAt: number;
};

const TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();

// Key the cache by the active org id so switching orgs doesn't return a stale
// "no files" from the previous org. `null` is a valid key for the worker's
// "no header" fallback path.
function cacheKey(activeOrgId: string | null): string {
  return activeOrgId ?? "__none__";
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

export function getCachedHasFiles(activeOrgId: string | null): boolean | null {
  const hit = cache.get(cacheKey(activeOrgId));
  if (hit && isFresh(hit)) return hit.hasFiles;
  return null;
}

export async function fetchHasFiles(activeOrgId: string | null): Promise<boolean> {
  const data = await apiGet<FilesResponse>("/api/v1/files?limit=1");
  const hasFiles = Array.isArray(data.items) && data.items.length > 0;
  cache.set(cacheKey(activeOrgId), { hasFiles, fetchedAt: Date.now() });
  return hasFiles;
}

// Test-only helpers. Not exported in production paths but harmless to ship.
export function setCachedHasFiles(activeOrgId: string | null, hasFiles: boolean): void {
  cache.set(cacheKey(activeOrgId), { hasFiles, fetchedAt: Date.now() });
}

export function clearFileCountCache(): void {
  cache.clear();
}
